import { ChangeDetectorRef, Component, NgZone, inject } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Api } from 'src/app/services/api/api.service';
import { ProductService } from 'src/app/services/product/product.service';
import { UserService } from 'src/app/services/user/user.service';
import _ from 'lodash';
import { Product } from 'src/app/models/Product';
import { Files } from 'package-types';
import { ModalController } from '@ionic/angular';
import { EditComboModalComponent } from '@components/edit-combo-modal/edit-combo-modal.component';
import { NotificationService } from 'src/app/services/notification/notification.service';
import { ConfirmationModalComponent } from '@components/confirmation-modal/confirmation-modal.component';

@Component({
  selector: 'app-combos',
  templateUrl: './combos.component.html',
  styleUrls: ['./combos.component.css'],
  standalone: false,
})
export class CombosComponent {
  userService = inject(UserService);
  i18n = inject(TranslateService);
  router = inject(Router);
  modalController = inject(ModalController);
  private productService = inject(ProductService);
  private notificationService = inject(NotificationService);
  private ngZone = inject(NgZone);
  private cdRef = inject(ChangeDetectorRef);

  activeMerchantId: string | undefined = undefined;
  combos = [];
  products: Product[];
  imagesLinks: Files.Images.IdDimensionsLinks;

  async ionViewWillEnter() {
    await this.ngZone.run(async () => {
      this.combos = [];
      this.products = [];
      this.imagesLinks = {};
      this.activeMerchantId =
        this.userService.merchants.find((m) => m.enableComboOffers)?._id ||
        this.userService.merchants[0]?._id;
      await this.fetchCombos();
    });
  }

  async fetchCombos() {
    const { comboOffers } = await Api.getCombos(this.activeMerchantId);
    this.combos = comboOffers;
    if (this.combos.length === 0) {
      return;
    }

    const params = {
      merchantId: this.activeMerchantId,
      merProductIds: _.flatten(
        this.combos.map((order) => order.products.map((product) => product.merProductId)),
      ),
    };
    const { products, imagesLinks } = await this.productService.getFilteredProducts(params);

    this.products = products;
    this.imagesLinks = imagesLinks;
    this.cdRef.detectChanges();
  }

  async onMerchantChange(ev) {
    this.combos = [];
    this.products = [];
    this.imagesLinks = {};
    this.activeMerchantId = ev.target.value;
    await this.fetchCombos();
    this.cdRef.detectChanges();
  }

  async openComboModal(comboToEdit = null) {
    const modal = await this.modalController.create({
      component: EditComboModalComponent,
      componentProps: {
        comboToEdit,
        products: comboToEdit ? this.products : [],
        images: comboToEdit ? this.imagesLinks : {},
        merchantId: this.activeMerchantId,
      },
      cssClass: 'subsystem-modal',
    });
    await modal.present();

    const { data } = await modal.onDidDismiss();
    if (data) {
      const { action, comboOffer, newProducts, newImages } = data;
      this.products = [...this.products, ...newProducts];
      this.imagesLinks = { ...this.imagesLinks, ...newImages };
      if (action === 'create') {
        this.combos.unshift(comboOffer);
      }
      if (action === 'edit') {
        this.combos.splice(
          this.combos.findIndex((c) => c._id === comboOffer._id),
          1,
          comboOffer,
        );
      }
      this.cdRef.detectChanges();
    }
  }

  async removeCombo(id) {
    const { success } = await ConfirmationModalComponent.show(
      this.modalController,
      'Remove Combo?',
      'This action cannot be undone',
    );
    if (!success) return;

    await Api.removeCombo(id);
    this.combos = this.combos.filter((c) => c._id !== id);
    this.cdRef.detectChanges();
    await this.notificationService.notify('Combo was removed');
  }
}
